import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { 
  Calculator, BarChart3, DollarSign, Package, TrendingUp, Calendar, FileText, Download 
} from 'lucide-react';
import LoadingSpinner from '../../ui/LoadingSpinner';
import {
  getCalculations
} from '../../../redux/api';
import { su } from '../../inventory/stockUi';

const CalculationsView = () => {
  const { t } = useTranslation();
  const [calculations, setCalculations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchCalculations();
  }, [dateRange]);

  const fetchCalculations = async () => {
    try {
      setLoading(true);
      const response = await getCalculations(dateRange);
      setCalculations(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri učitavanju kalkulacija');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateCSV = () => {
    if (!calculations) return '';
    const lines = [
      'Kategorija,Vrednost',
      `Ukupno artikala,${calculations.inventory?.totalItems || 0}`,
      `Ukupna količina,${calculations.inventory?.totalQuantity || 0}`,
      `Ukupna vrednost zaliha,${calculations.inventory?.totalValue || 0}`,
      `Ulazi - broj,${calculations.transactions?.inputs?.count || 0}`,
      `Ulazi - količina,${calculations.transactions?.inputs?.quantity || 0}`,
      `Ulazi - vrednost,${calculations.transactions?.inputs?.value || 0}`,
      `Izlazi - broj,${calculations.transactions?.outputs?.count || 0}`,
      `Izlazi - količina,${calculations.transactions?.outputs?.quantity || 0}`,
      `Izlazi - vrednost,${calculations.transactions?.outputs?.value || 0}`,
      `Neto promena količine,${calculations.transactions?.netChange || 0}`,
      `Neto promena vrednosti,${calculations.transactions?.netValue || 0}`,
      `Ulazni PDV,${calculations.vat?.inputVAT || 0}`,
      `Ukupan PDV,${calculations.vat?.totalVAT || 0}`,
      `Uvozni obračuni,${calculations.customs?.totalDeclarations || 0}`,
      `Carina,${calculations.customs?.totalCustomsDuty || 0}`,
      `PDV na uvoz,${calculations.customs?.totalCustomsVAT || 0}`,
      `Ukupno carina,${calculations.customs?.totalCustomsAmount || 0}`,
      `Trošak materijala,${calculations.jobMaterials?.totalCost || 0}`,
      `Prihod materijala,${calculations.jobMaterials?.totalRevenue || 0}`,
      `Marža materijala,${calculations.jobMaterials?.totalMargin || 0}`,
      `Marža %,${calculations.jobMaterials?.marginPercentage?.toFixed(2) || 0}`
    ];
    return lines.join('\n');
  };

  if (loading) {
    return (
      <div className={`${su.page} flex items-center justify-center`}>
        <LoadingSpinner size="lg" color="white" />
      </div>
    );
  }

  if (!calculations) {
    return null;
  }

  return (
    <div className={su.page}>
      <div className={su.wrap}>
        <div className={`${su.card} ${su.cardPad}`}>
          <div className={su.toolbar}>
            <div className="min-w-0 flex-1">
              <h1 className={su.title}>
                <Calculator className={su.icon} />
                {t('inventory.calculations.title', 'Kalkulacije i izveštaji')}
              </h1>
              <p className={su.subtitle}>
                {t('inventory.calculations.subtitle', 'Pregled vrednovanja zaliha, transakcija i kalkulacija')}
              </p>
            </div>
            <input
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateChange}
              className={`${su.input} ${su.date}`}
              style={{ colorScheme: 'dark' }}
            />
            <input
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateChange}
              className={`${su.input} ${su.date}`}
              style={{ colorScheme: 'dark' }}
            />
            <button
              onClick={() => {
                const csv = generateCSV();
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `kalkulacije_${dateRange.startDate}_${dateRange.endDate}.csv`;
                a.click();
              }}
              className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium bg-green-600 hover:bg-green-500 text-white rounded-md transition shrink-0"
            >
              <Download className="h-3.5 w-3.5" />
              {t('common.export', 'CSV')}
            </button>
          </div>
        </div>

        {/* Inventory Valuation */}
        <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
          <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t('inventory.calculations.inventoryValuation', 'Vrednovanje zaliha')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.totalItems', 'Ukupno artikala')}</div>
              <div className="text-base font-semibold text-white mt-0.5">{calculations.inventory?.totalItems || 0}</div>
              <div className="text-xs text-gray-500 mt-1">
                Magacin: {calculations.inventory?.warehouseItems || 0} · Rez. delovi: {calculations.inventory?.sparePartsItems || 0}
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.totalQuantity', 'Ukupna količina')}</div>
              <div className="text-base font-semibold text-white mt-0.5">{calculations.inventory?.totalQuantity || 0}</div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.totalValue', 'Ukupna vrednost')}</div>
              <div className="text-base font-semibold text-white mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.inventory?.totalValue || 0)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Magacin: {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.inventory?.warehouseValue || 0)}
                {' · '}
                Rez. delovi: {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.inventory?.sparePartsValue || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Summary */}
        <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
          <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {t('inventory.calculations.transactionsSummary', 'Sažetak transakcija')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">
                {t('inventory.inputs.title', 'Ulazi')}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('common.count', 'Broj')}:</span>
                  <span className="text-white">{calculations.transactions?.inputs?.count || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('inventory.quantity', 'Količina')}:</span>
                  <span className="text-white">{calculations.transactions?.inputs?.quantity || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('inventory.value', 'Vrednost')}:</span>
                  <span className="text-white font-bold">
                    {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.transactions?.inputs?.value || 0)}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">
                {t('inventory.outputs.title', 'Izlazi')}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('common.count', 'Broj')}:</span>
                  <span className="text-white">{calculations.transactions?.outputs?.count || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('inventory.quantity', 'Količina')}:</span>
                  <span className="text-white">{calculations.transactions?.outputs?.quantity || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{t('inventory.value', 'Vrednost')}:</span>
                  <span className="text-white font-bold">
                    {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.transactions?.outputs?.value || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
                <div className="text-sm text-gray-400">{t('inventory.calculations.netChange', 'Neto promena količine')}</div>
                <div className="text-sm font-semibold text-white mt-1">
                  {calculations.transactions?.netChange || 0}
                </div>
              </div>
              <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
                <div className="text-sm text-gray-400">{t('inventory.calculations.netValue', 'Neto promena vrednosti')}</div>
                <div className="text-sm font-semibold text-white mt-1">
                  {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.transactions?.netValue || 0)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VAT Summary */}
        <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
          <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            {t('inventory.calculations.vatSummary', 'PDV sažetak')}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.calculations.inputVAT', 'Ulazni PDV')}</div>
              <div className="text-base font-semibold text-white mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.vat?.inputVAT || 0)}
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.calculations.totalVAT', 'Ukupan PDV')}</div>
              <div className="text-base font-semibold text-white mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.vat?.totalVAT || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Customs Summary */}
        <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
          <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('inventory.customs.title', 'Uvoz')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.calculations.totalDeclarations', 'Ukupno uvoznih obračuna')}</div>
              <div className="text-base font-semibold text-white mt-0.5">{calculations.customs?.totalDeclarations || 0}</div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.customs.customsDuty', 'Carina')}</div>
              <div className="text-base font-semibold text-white mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.customs?.totalCustomsDuty || 0)}
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.customs.vatAmount', 'PDV na uvoz')}</div>
              <div className="text-base font-semibold text-white mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.customs?.totalCustomsVAT || 0)}
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.customs.totalAmount', 'Ukupno')}</div>
              <div className="text-base font-semibold text-white mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.customs?.totalCustomsAmount || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Job Materials */}
        <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
          <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {t('inventory.calculations.jobMaterials', 'Materijal za poslove')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.calculations.movementsCount', 'Broj pokreta')}</div>
              <div className="text-base font-semibold text-white mt-0.5">{calculations.jobMaterials?.movementsCount || 0}</div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.withdrawn.issued', 'Izdata')}</div>
              <div className="text-sm font-semibold text-green-400 mt-0.5">{calculations.jobMaterials?.issuedCount || 0}</div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.withdrawn.used', 'Iskorišćeno')}</div>
              <div className="text-sm font-semibold text-purple-400 mt-0.5">{calculations.jobMaterials?.usedCount || 0}</div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.calculations.totalCost', 'Ukupni trošak')}</div>
              <div className="text-base font-semibold text-white mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.jobMaterials?.totalCost || 0)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.calculations.totalRevenue', 'Ukupan prihod')}</div>
              <div className="text-base font-semibold text-white mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.jobMaterials?.totalRevenue || 0)}
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.calculations.totalMargin', 'Ukupna marža')}</div>
              <div className="text-sm font-semibold text-green-400 mt-0.5">
                {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(calculations.jobMaterials?.totalMargin || 0)}
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-md px-2.5 py-2">
              <div className="text-sm text-gray-400">{t('inventory.calculations.marginPercentage', 'Marža (%)')}</div>
              <div className="text-sm font-semibold text-green-400 mt-0.5">
                {calculations.jobMaterials?.marginPercentage?.toFixed(2) || 0}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculationsView;

